import { useSnapshot } from 'valtio';
import ServiceModule from '.';
import { ReadonlyServiceQueue } from './workspace/types/ServiceQueue';
import ServiceSession from './workspace/types/ServiceSession';
import ServiceUser from './workspace/types/ServiceUser';
import { serviceState } from './workspace/lib/state';
import { mapQueueIdToUsers } from '@pages/monitoring/QueuesPage/utils/users';

export type UseServiceResultType = {
  agentId: string;
  agentAvailable: boolean;
  readonly users: Readonly<ServiceUser[]>;
  readonly userById: Readonly<Record<string, ServiceUser>>;
  readonly userByContactCenterUserId: Readonly<Record<string, ServiceUser>>;
  queues: Readonly<ReadonlyServiceQueue[]>;
  queueById: Readonly<Record<string, ReadonlyServiceQueue>>;
  sessions: Readonly<ServiceSession[]>;
  loading: boolean;
  loadingStatus: string;
  serviceConnected: boolean;
  error: unknown;
  refetch: () => void;
};

function useService(): UseServiceResultType {
  const {
    users,
    userById,
    userByContactCenterUserId,
    queues,
    queueById,
    sessions,
    loading,
    loadingStatus,
    error,
    serviceConnected,
  } = useSnapshot(serviceState);
  const currentUser = userById[serviceState.id ?? ''];
  let agentAvailable = false;
  if (currentUser) {
    agentAvailable = ServiceModule.isUserAvailable(currentUser) || false;
  }
  return {
    agentId: serviceState.id!,
    agentAvailable,
    users,
    userById,
    userByContactCenterUserId,
    queues,
    queueById,
    sessions: Object.values(sessions),
    loading,
    loadingStatus,
    serviceConnected,
    error,
    refetch: () => ServiceModule.refetchWorkspace(),
  };
}

export type UseServiceQueuesType = {
  queues: Readonly<ReadonlyServiceQueue[]>;
  loading: boolean;
  error: unknown;
  queueById: Record<string, ReadonlyServiceQueue>;
};

export function useQueues(): UseServiceQueuesType {
  const { queues, queueById, loading, error } = useSnapshot(serviceState);

  return {
    queues,
    loading,
    error,
    queueById,
  };
}

export type UseServiceUsersType = {
  sessions: Readonly<ServiceSession[]>;
  queueIdToUsers: Record<string, ServiceUser[]>;
};

export function useUsers(): UseServiceUsersType {
  const { userByContactCenterUserId, sessions, queues } =
    useSnapshot(serviceState);

  return {
    sessions: Object.values(sessions),
    queueIdToUsers: mapQueueIdToUsers(queues, userByContactCenterUserId) ?? {},
  };
}

export default useService;
